import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";
import { extractFlattenedFieldAPI } from "../../../../Utils/WatchlistSummary/validation";

// ===== TYPES & INTERFACES =====

interface SectionData {
  sectionScore?: number;
  sectionHealthFlag?: string;
  sectionRelevanceFlag?: string;
}

interface Technology {
  name?: string;
  description?: string;
  category?: string;
}

interface Initiative {
  name?: string;
  description?: string;
  category?: string;
}

interface TechnologyCounts {
  total_technologies?: number;
  vendors?: number;
  product_categories?: number;
}

interface StackSectionProps {
  data: any;
  sectionData: any;
  accountName: string;
  organizationName?: string;
}

// ===== SCORE BUBBLE COMPONENT =====

const StackScoreBubble: React.FC<{ score: number }> = ({ score }) => {
  const getScoreColors = (score: number) => businessHealthScorePalette(score);

  const colors = getScoreColors(score);

  return (
    <div style={{ position: "relative", width: 48, height: 48, flexShrink: 0 }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: colors.outer,
          border: `2px solid ${colors.inner}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 6,
          left: 6,
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: colors.inner,
          border: `1px solid ${colors.score}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -2,
          right: -2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: colors.score,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {Math.round(score)}
        </span>
      </div>
    </div>
  );
};

// ===== MODAL HEADER COMPONENT =====

const StackModalHeader: React.FC<{
  accountName: string;
  sectionData: SectionData;
  onToggleAudio?: () => void;
}> = ({ accountName, sectionData, onToggleAudio }) => {
  const sectionScore = sectionData.sectionScore || 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "16px",
      }}
    >
      <StackScoreBubble score={sectionScore} />
      <div>
        <h3 style={{ margin: 0, fontSize: 18, color: "#8b5cf6" }}>
          <strong>{accountName}</strong> - <em>Technology Stack</em>
        </h3>
      </div>
      <button
        id="stack-audio-btn"
        title="Play stack audio narration"
        onClick={onToggleAudio}
        style={{
          background: "none",
          border: "none",
          padding: 8,
          borderRadius: 6,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#8b5cf6",
        }}
      >
        <svg
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 5 6 9H2v6h4l5 4V5Z" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </button>
    </div>
  );
};

// ===== MODAL BODY COMPONENT =====

const StackModalBody: React.FC<{
  synopsis?: string | null;
  confidenceScore?: number | null;
  dataFreshness?: string | null;
  lastUpdated?: string | null;
  sources?: any[];
  sectionData: SectionData;
  organizationName?: string;
}> = ({
  synopsis,
  confidenceScore,
  dataFreshness,
  sources,
  sectionData,
  organizationName,
}) => {
  const healthFlag = sectionData.sectionHealthFlag || "N/A";
  const relevanceFlag = sectionData.sectionRelevanceFlag || "N/A";

  const sourcesText = Array.isArray(sources)
    ? sources
        .map(s => (typeof s === "object" ? s?.name : s))
        .filter(Boolean)
        .join(", ")
    : null;

  const formatConfidenceScore = (score: number | null | undefined) => {
    if (score === null || score === undefined)
      return { text: "N/A", color: "#64748b" };
    if (typeof score === "number") {
      const percentage =
        score <= 1 ? Math.round(score * 100) : Math.round(score);
      if (percentage >= 80) return { text: `${percentage}%`, color: "#16a34a" };
      if (percentage >= 50) return { text: `${percentage}%`, color: "#d97706" };
      return { text: `${percentage}%`, color: "#dc2626" };
    }
    return { text: "N/A", color: "#64748b" };
  };

  const formatFreshness = (freshness: any) => {
    if (!freshness || freshness === "N/A")
      return { text: "N/A", color: "#64748b" };
    const freshnessStr = String(freshness).toLowerCase();
    if (
      freshnessStr.includes("high") ||
      freshnessStr.includes("recent") ||
      freshnessStr.includes("fresh") ||
      freshnessStr.includes("current")
    ) {
      return { text: freshness, color: "#16a34a" };
    }
    if (
      freshnessStr.includes("low") ||
      freshnessStr.includes("stale") ||
      freshnessStr.includes("old") ||
      freshnessStr.includes("outdated")
    ) {
      return { text: freshness, color: "#dc2626" };
    }
    if (freshnessStr.includes("medium") || freshnessStr.includes("moderate")) {
      return { text: freshness, color: "#d97706" };
    }
    return { text: freshness, color: "#64748b" };
  };

  const confidenceFormatted = formatConfidenceScore(confidenceScore);
  const freshnessFormatted = formatFreshness(dataFreshness);

  const getHealthText = (flag: string) => {
    switch (flag) {
      case "G":
        return "GREEN";
      case "Y":
        return "YELLOW";
      case "R":
        return "RED";
      default:
        return "N/A";
    }
  };

  const getRelevanceText = (flag: string) => {
    switch (flag) {
      case "H":
        return "HIGH";
      case "M":
        return "MEDIUM";
      case "L":
        return "LOW";
      default:
        return "N/A";
    }
  };

  const badgeStyle = (bgColor: string) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 12px",
    background: bgColor,
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 500,
    color: "white",
  });

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "12px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          style={badgeStyle(
            healthFlag === "G"
              ? "#16a34a"
              : healthFlag === "Y"
                ? "#d97706"
                : "#dc2626"
          )}
        >
          <span>Account Health:</span>
          <strong>{getHealthText(healthFlag)}</strong>
        </div>
        <div style={badgeStyle("#8b5cf6")}>
          <span>Relevance to {organizationName || "Organization"}:</span>
          <strong>{getRelevanceText(relevanceFlag)}</strong>
        </div>
      </div>

      {synopsis ? (
        <div style={{ padding: 16 }}>
          <p
            style={{
              margin: "0 0 12px 0",
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {synopsis}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 12,
              marginTop: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Confidence:
              </span>
              <span
                style={{
                  color: confidenceFormatted.color,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {confidenceFormatted.text}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: 4,
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Freshness:
              </span>
              <span
                style={{
                  color: freshnessFormatted.color,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {freshnessFormatted.text}
              </span>
            </div>
            {sourcesText && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  padding: "6px 8px",
                  background: "#f8fafc",
                  borderRadius: 4,
                  gridColumn: "span 2",
                }}
              >
                <span style={{ color: "#64748b", fontWeight: 500 }}>
                  Sources:
                </span>
                <span
                  style={{
                    color: "#1e293b",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    textAlign: "right",
                  }}
                >
                  {sourcesText}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ padding: 16, textAlign: "center", color: "#94a3b8" }}>
          No summary information available
        </div>
      )}
    </div>
  );
};

// ===== ORGANIZATION RELEVANCE SECTION =====

const OrganizationRelevanceSection: React.FC<{
  organizationRelevance?: string | null;
  organizationName?: string;
}> = ({ organizationRelevance, organizationName }) => {
  if (!organizationRelevance) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🎯</span> Relevance for {organizationName || "Organization"}
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div
          style={{
            padding: 16,
            background: "#eff6ff",
            border: "1px solid #dbeafe",
            borderRadius: 8,
          }}
        >
          <p
            style={{
              margin: 0,
              lineHeight: 1.6,
              fontSize: 14,
              color: "#1e40af",
            }}
          >
            {organizationRelevance}
          </p>
        </div>
      </div>
    </div>
  );
};

// ===== KEY METRICS SECTION =====

const KeyMetricsSection: React.FC<{
  totalTechnologies?: number;
  vendors?: number;
  productCategories?: number;
}> = ({ totalTechnologies, vendors, productCategories }) => {
  if (totalTechnologies == null && vendors == null && productCategories == null)
    return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>📊</span> Key Technology Metrics
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 12,
          }}
        >
          {totalTechnologies != null && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#eff6ff",
                borderRadius: 8,
                border: "1px solid #dbeafe",
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700, color: "#1d4ed8" }}>
                {totalTechnologies}
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Total Technologies
              </div>
            </div>
          )}
          {vendors != null && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#f0fdf4",
                borderRadius: 8,
                border: "1px solid #dcfce7",
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700, color: "#16a34a" }}>
                {vendors}
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>Vendors</div>
            </div>
          )}
          {productCategories != null && (
            <div
              style={{
                textAlign: "center",
                padding: 12,
                background: "#fefce8",
                borderRadius: 8,
                border: "1px solid #fef08a",
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700, color: "#ca8a04" }}>
                {productCategories}
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Product Categories
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== TECHNOLOGY CATEGORIES SECTION =====

const TechnologyCategoriesSection: React.FC<{
  corePlatforms?: Technology[];
  cloudInfrastructure?: Technology[];
  dataAnalytics?: Technology[];
  enterpriseApplications?: Technology[];
  securityGovernance?: Technology[];
  developmentTools?: Technology[];
  dataScienceTools?: Technology[];
}> = ({
  corePlatforms,
  cloudInfrastructure,
  dataAnalytics,
  enterpriseApplications,
  securityGovernance,
  developmentTools,
  dataScienceTools,
}) => {
  const allCategories = [
    { title: "🏗️ Core Platforms", items: corePlatforms, color: "#3b82f6" },
    {
      title: "☁️ Cloud & Infrastructure",
      items: cloudInfrastructure,
      color: "#06b6d4",
    },
    { title: "📈 Data & Analytics", items: dataAnalytics, color: "#8b5cf6" },
    {
      title: "💼 Enterprise Applications",
      items: enterpriseApplications,
      color: "#10b981",
    },
    {
      title: "🔒 Security & Governance",
      items: securityGovernance,
      color: "#f59e0b",
    },
    {
      title: "⚙️ Development Tools",
      items: developmentTools,
      color: "#ef4444",
    },
    {
      title: "🔬 Data Science Tools",
      items: dataScienceTools,
      color: "#84cc16",
    },
  ];

  const validCategories = allCategories.filter(
    cat => cat.items && Array.isArray(cat.items) && cat.items.length > 0
  );

  if (validCategories.length === 0) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🛠️</span> Technology Stack Categories
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        {validCategories.map((category, i) => (
          <div
            key={i}
            style={{
              marginBottom: 20,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: category.color,
                padding: 12,
                color: "white",
              }}
            >
              <h5 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                {category.title}
              </h5>
            </div>
            <div style={{ padding: 12 }}>
              {category.items?.map((item, j) => (
                <div
                  key={j}
                  style={{
                    marginBottom: 8,
                    padding: 8,
                    background: "#f8fafc",
                    borderRadius: 6,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#374151",
                      marginBottom: 4,
                    }}
                  >
                    {item.name || "Unnamed Technology"}
                  </div>
                  {item.description && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#64748b",
                        lineHeight: 1.4,
                      }}
                    >
                      {item.description}
                    </div>
                  )}
                  {item.category && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "#8b5cf6",
                        marginTop: 4,
                        fontWeight: 500,
                      }}
                    >
                      Category: {item.category}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== INITIATIVES SECTION =====

const InitiativesSection: React.FC<{
  notableInitiatives?: Initiative[];
  digitalTransformation?: Initiative[];
}> = ({ notableInitiatives, digitalTransformation }) => {
  const hasInitiatives =
    notableInitiatives &&
    Array.isArray(notableInitiatives) &&
    notableInitiatives.length > 0;
  const hasTransformation =
    digitalTransformation &&
    Array.isArray(digitalTransformation) &&
    digitalTransformation.length > 0;

  if (!hasInitiatives && !hasTransformation) return null;

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 16px 8px 16px" }}>
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: 16,
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>🚀</span> Technology Initiatives & Digital Transformation
        </h4>
      </div>
      <div style={{ padding: 16 }}>
        {hasInitiatives && notableInitiatives && (
          <div style={{ marginBottom: 16 }}>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#16a34a",
              }}
            >
              Notable Initiatives
            </h5>
            {notableInitiatives.map((item, i) => (
              <div
                key={i}
                style={{
                  marginBottom: 8,
                  padding: 12,
                  background: "#f0fdf4",
                  border: "1px solid #dcfce7",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#15803d",
                    marginBottom: 4,
                  }}
                >
                  {item.name || "Unnamed Initiative"}
                </div>
                {item.description && (
                  <div
                    style={{ fontSize: 12, color: "#374151", lineHeight: 1.4 }}
                  >
                    {item.description}
                  </div>
                )}
                {item.category && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#16a34a",
                      marginTop: 4,
                      fontWeight: 500,
                    }}
                  >
                    Type: {item.category}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {hasTransformation && digitalTransformation && (
          <div>
            <h5
              style={{
                margin: "0 0 12px 0",
                fontSize: 14,
                fontWeight: 600,
                color: "#8b5cf6",
              }}
            >
              Digital Transformation
            </h5>
            {digitalTransformation.map((item, i) => (
              <div
                key={i}
                style={{
                  marginBottom: 8,
                  padding: 12,
                  background: "#faf5ff",
                  border: "1px solid #e9d5ff",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#7c3aed",
                    marginBottom: 4,
                  }}
                >
                  {item.name || "Unnamed Transformation"}
                </div>
                {item.description && (
                  <div
                    style={{ fontSize: 12, color: "#374151", lineHeight: 1.4 }}
                  >
                    {item.description}
                  </div>
                )}
                {item.category && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#8b5cf6",
                      marginTop: 4,
                      fontWeight: 500,
                    }}
                  >
                    Type: {item.category}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ===== MAIN STACK SECTION COMPONENT =====

export const StackSection: React.FC<StackSectionProps> = ({
  data,
  sectionData,
  accountName,
  organizationName,
}) => {
  if (!data) return <div>No stack data available</div>;

  const synopsis = extractFlattenedFieldAPI(data, "synopsis", null);
  const confidenceScore = extractFlattenedFieldAPI(
    data,
    "metadata.confidence_score",
    null
  );
  const lastUpdated = extractFlattenedFieldAPI(
    data,
    "metadata.last_updated",
    null
  );
  const dataFreshness = extractFlattenedFieldAPI(
    data,
    "metadata.data_freshness",
    null
  );
  const sources = extractFlattenedFieldAPI(data, "metadata.sources", []);

  const technologyCounts: TechnologyCounts = extractFlattenedFieldAPI(
    data,
    "metrics.technology_counts",
    {}
  );
  const totalTechnologies = technologyCounts?.total_technologies;
  const vendors = technologyCounts?.vendors;
  const productCategories = technologyCounts?.product_categories;

  const corePlatforms = extractFlattenedFieldAPI(
    data,
    "technology_stack.core_platforms",
    []
  );
  const cloudInfrastructure = extractFlattenedFieldAPI(
    data,
    "technology_stack.cloud_and_infrastructure",
    []
  );
  const dataAnalytics = extractFlattenedFieldAPI(
    data,
    "technology_stack.data_and_analytics",
    []
  );
  const enterpriseApplications = extractFlattenedFieldAPI(
    data,
    "technology_stack.enterprise_applications",
    []
  );
  const securityGovernance = extractFlattenedFieldAPI(
    data,
    "technology_stack.security_and_governance",
    []
  );
  const developmentTools = extractFlattenedFieldAPI(
    data,
    "technology_stack.development_tools",
    []
  );
  const dataScienceTools = extractFlattenedFieldAPI(
    data,
    "technology_stack.data_science_tools",
    []
  );

  const notableInitiatives = extractFlattenedFieldAPI(
    data,
    "technology_stack.notable_initiatives",
    []
  );
  const digitalTransformation = extractFlattenedFieldAPI(
    data,
    "technology_stack.recent_digital_transformation",
    []
  );

  const organizationRelevance = extractFlattenedFieldAPI(
    data,
    "organizationRelevance.description",
    null
  );

  const handleToggleAudio = () => {
    return;
  };

  return (
    <div className="stack-section-consolidated">
      <StackModalHeader
        accountName={accountName}
        sectionData={sectionData}
        onToggleAudio={handleToggleAudio}
      />
      <StackModalBody
        synopsis={synopsis}
        confidenceScore={confidenceScore}
        dataFreshness={dataFreshness}
        lastUpdated={lastUpdated}
        sources={sources}
        sectionData={sectionData}
        organizationName={organizationName}
      />
      <OrganizationRelevanceSection
        organizationRelevance={organizationRelevance}
        organizationName={organizationName}
      />
      <KeyMetricsSection
        totalTechnologies={totalTechnologies}
        vendors={vendors}
        productCategories={productCategories}
      />
      <TechnologyCategoriesSection
        corePlatforms={corePlatforms}
        cloudInfrastructure={cloudInfrastructure}
        dataAnalytics={dataAnalytics}
        enterpriseApplications={enterpriseApplications}
        securityGovernance={securityGovernance}
        developmentTools={developmentTools}
        dataScienceTools={dataScienceTools}
      />
      <InitiativesSection
        notableInitiatives={notableInitiatives}
        digitalTransformation={digitalTransformation}
      />
    </div>
  );
};
